import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, Download } from "lucide-react";
import api, { FILE_BASE } from "../lib/api";
import Header from "../components/Header";

export default function Receipt() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!email) {
      setErr("E-post saknas i URL:en.");
      return;
    }
    api.get(`/receipts/${orderId}`, { params: { email } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Kvitto kunde inte hämtas"));
  }, [orderId, email]);

  if (err) return <ReceiptError message={err} />;
  if (!data) return <ReceiptLoading />;

  const { order, items, platform } = data;

  return (
    <div className="App min-h-screen bg-[#0a0a0a] print:bg-white">
      <div className="print:hidden">
        <Header />
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 print:py-4">
        <div className="flex justify-between items-center mb-6 print:hidden">
          <h1 className="font-display font-black uppercase text-white text-3xl">Kvitto</h1>
          <button onClick={() => window.print()} className="btn-primary flex items-center gap-2" data-testid="print-receipt">
            <Printer className="w-4 h-4" /> Skriv ut / spara PDF
          </button>
        </div>

        <ReceiptLayout order={order} items={items} sellerInfo={platform} />
      </div>
    </div>
  );
}

export function ReceiptLayout({ order, items, sellerInfo }) {
  const seller = sellerInfo || {
    name: "Kimmo Ilvonen Konsult AB",
    org: "559205-3432",
    address: "Torpvägen 20, 147 63 Uttran",
    email: "foton@enduroskola.se",
  };
  const total = Number(order.total_amount) || 0;
  const vat = Number(order.vat_amount) || 0;
  const discount = Number(order.discount) || 0;
  const listTotal = Number(order.list_total) || total;
  const subtotalExVat = (total - vat).toFixed(2);

  return (
    <div className="bg-white text-black p-8 sm:p-12 shadow-xl print:shadow-none print:p-0" data-testid="receipt-layout">
      <div className="flex items-start justify-between mb-8 pb-6 border-b border-black/10">
        <div>
          <h2 className="font-display font-black uppercase text-3xl">Kvitto / Receipt</h2>
          <p className="text-sm text-black/60 mt-1">Order-ID: <span className="font-mono">{order.id}</span></p>
          <p className="text-sm text-black/60">Datum: {new Date(order.created_at).toLocaleDateString("sv-SE")}</p>
        </div>
        <div className="text-right">
          <p className="font-display font-bold uppercase">{seller.name}</p>
          <p className="text-xs text-black/60">Org.nr: {seller.org}</p>
          {seller.address && <p className="text-xs text-black/60">{seller.address}</p>}
          <p className="text-xs text-black/60">{seller.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 mb-8 text-sm">
        <div>
          <p className="uppercase text-xs tracking-wider text-black/50 mb-1">Köpare</p>
          <p className="font-bold">{order.customer_name}</p>
          <p>{order.customer_email}</p>
          {order.customer_phone && <p className="text-black/60">{order.customer_phone}</p>}
        </div>
        <div>
          <p className="uppercase text-xs tracking-wider text-black/50 mb-1">Betalning</p>
          <p>Status: <strong>{order.status === "paid" ? "Betald" : order.status}</strong></p>
          <p>Metod: Stripe (kort)</p>
          <p className="text-black/60 text-xs font-mono">{order.session_id?.slice(0, 24)}...</p>
        </div>
      </div>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="border-b-2 border-black/20 text-left uppercase text-xs tracking-wider">
            <th className="py-2">Foto</th>
            <th className="py-2">Fotograf</th>
            <th className="py-2 text-right">Pris ex moms</th>
            <th className="py-2 text-right">Moms 25%</th>
            <th className="py-2 text-right">Summa</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const price = Number(it.price) || 0;
            const vatItem = Number(it.vat) || (price * (0.25 / 1.25));
            const ex = (price - vatItem).toFixed(2);
            return (
              <tr key={i} className="border-b border-black/5">
                <td className="py-2 flex items-center gap-2">
                  {it.photo_thumb_url && (
                    <img src={`${FILE_BASE}${it.photo_thumb_url}`} alt="" className="w-10 h-8 object-cover" />
                  )}
                  <span className="font-mono text-xs text-black/60">{(it.photo_id || "").slice(0, 8)}</span>
                </td>
                <td className="py-2">
                  {it.photographer_name}
                  {it.photographer_org && <span className="block text-xs text-black/40">{it.photographer_org}</span>}
                </td>
                <td className="py-2 text-right">{ex} kr</td>
                <td className="py-2 text-right">{vatItem.toFixed(2)} kr</td>
                <td className="py-2 text-right font-bold">{price.toFixed(2)} kr</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex justify-end">
        <div className="w-full sm:w-80 space-y-1 text-sm">
          {discount > 0 && (
            <div className="flex justify-between">
              <span>Listpris</span>
              <span>{listTotal.toFixed(2)} kr</span>
            </div>
          )}
          {discount > 0 && (
            <div className="flex justify-between text-green-700">
              <span>Bundle-rabatt</span>
              <span>−{discount.toFixed(2)} kr</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Delsumma ex moms</span>
            <span>{subtotalExVat} kr</span>
          </div>
          <div className="flex justify-between">
            <span>Moms 25%</span>
            <span>{vat.toFixed(2)} kr</span>
          </div>
          <div className="flex justify-between font-display font-black text-lg pt-2 border-t-2 border-black/20">
            <span>TOTALT</span>
            <span>{total.toFixed(2)} kr</span>
          </div>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-black/10 text-xs text-black/50 space-y-1">
        <p>Digitala foton – nedladdningsbara. Ångerrätten upphör vid nedladdning av originalfil (distansavtalslagen 2 kap 11§ p.11).</p>
        <p>Vid frågor, kontakta {seller.email}. Spara kvittot för ditt bokföringsunderlag.</p>
      </div>
    </div>
  );
}

function ReceiptError({ message }) {
  return (
    <div className="App min-h-screen flex items-center justify-center p-8">
      <div className="card-surface p-10 text-center">
        <p className="label-overline mb-2">Fel</p>
        <p className="font-display uppercase text-white text-2xl mb-2">Kvitto kunde inte hämtas</p>
        <p className="text-white/60 text-sm">{message}</p>
      </div>
    </div>
  );
}

function ReceiptLoading() {
  return (
    <div className="App min-h-screen flex items-center justify-center">
      <p className="text-white/50">Laddar kvitto...</p>
    </div>
  );
}
